import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { ArrowLeft, Edit, Users, TrendingUp, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const DepartmentDetail = () => {
  const user = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    if (!user?.is_manager && user?.role !== 'office_owner') {
      navigate('/');
      return;
    }

    fetchDepartment();
  }, [id]);

  const fetchDepartment = async () => {
    try {
      const response = await api.get(`/departments/${id}`);
      setDepartment(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Departman yüklenemedi:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  if (!department) {
    return <div className="text-center py-8">Departman bulunamadı</div>;
  }

  const userColumns = [
    { header: 'Ad Soyad', accessor: 'name' },
    { header: 'E-posta', accessor: 'email' },
    {
      header: 'Rol',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.is_manager ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
          }`}>
          {row.is_manager ? 'Yönetici' : 'Personel'}
        </span>
      )
    },
    {
      header: 'Durum',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
          {row.status === 'active' ? 'Aktif' : 'Pasif'}
        </span>
      )
    },
  ];

  const meetingColumns = [
    { header: 'Müşteri', render: (row) => `${row.customer?.name} ${row.customer?.surname}` },
    { header: 'Temsilci', render: (row) => row.user?.name },
    { header: 'Tarih', render: (row) => new Date(row.date).toLocaleDateString('tr-TR') },
    { header: 'Tip', accessor: 'type' },
  ];

  const salesColumns = [
    { header: 'Müşteri', render: (row) => `${row.customer?.name} ${row.customer?.surname}` },
    { header: 'Portföy', render: (row) => row.portfolio?.title },
    { header: 'Fiyat', render: (row) => `${row.final_sale_price?.toLocaleString('tr-TR')} ₺` },
    { header: 'Tarih', render: (row) => new Date(row.sale_date).toLocaleDateString('tr-TR') },
  ];

  const totalSales = department.users?.reduce((sum, user) =>
    sum + (user.sales?.length || 0), 0
  ) || 0;

  const totalMeetings = department.users?.reduce((sum, user) =>
    sum + (user.meetings?.length || 0), 0
  ) || 0;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => navigate('/departments')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
        <Button size="sm" onClick={() => navigate(`/departments/${id}/edit`)}>
          <Edit size={16} />
          Düzenle
        </Button>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{department.name}</h1>
            <p className="text-gray-600">
              {department.users?.length || 0} Personel
            </p>
          </div>
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <Users size={32} className="text-primary-600" />
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Toplam Personel</p>
              <p className="text-2xl font-bold text-gray-900">{department.users?.length || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Toplam Satış</p>
              <p className="text-2xl font-bold text-green-600">{totalSales}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <TrendingUp size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Toplam Görüşme</p>
              <p className="text-2xl font-bold text-purple-600">{totalMeetings}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <Calendar size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Personeller */}
      <Card title="Departman Personelleri" noPadding className="mb-6">
        {department.users && department.users.length > 0 ? (
          <Table 
            columns={userColumns} 
            data={department.users} 
            onView={(row) => navigate(`/departments/users/${row.id}`)}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">
            Bu departmanda personel bulunmuyor
          </div>
        )}
      </Card>

      {/* Son Görüşmeler */}
      <Card title="Son Görüşmeler" noPadding className="mb-6">
        {department.users?.flatMap(u => u.meetings || []).length > 0 ? (
          <Table
            columns={meetingColumns}
            data={department.users.flatMap(u => u.meetings || []).slice(0, 10)}
            onRowClick={(row) => navigate(`/meetings/${row.id}`)}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">
            Henüz görüşme kaydı yok
          </div>
        )}
      </Card>

      {/* Son Satışlar */}
      <Card title="Son Satışlar" noPadding>
        {department.users?.flatMap(u => u.sales || []).length > 0 ? (
          <Table
            columns={salesColumns}
            data={department.users.flatMap(u => u.sales || []).slice(0, 10)}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">
            Henüz satış kaydı yok
          </div>
        )}
      </Card>
    </div>
  );
};

export default DepartmentDetail;
